// Contexto de autenticación y permisos de la PWA (Req. 17.2, 18.1, 19.10).
//
// Guarda la sesión (tokens de Cognito) de forma persistente, expone el JWT para
// que el cliente de la API lo envíe en cada petición, carga el mapa de permisos
// del usuario (`GET /me/permissions`) y ofrece `hasPermission` para adaptar
// navegación y controles (Req. 19.10). Al cerrar sesión, limpia todo.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { CognitoTokens } from './cognito.js';
import { globalSignOut, refresh as refreshTokens, CognitoError } from './cognito.js';
import { ApiError } from '../api/client.js';
import type { Feature, MePermissions, PermissionLevel } from '../types.js';
import { useIdleTimeout, LAST_ACTIVITY_KEY } from './useIdleTimeout.js';
import { identifyUser, resetIdentity } from '../analytics/analytics.js';

/** Clave de almacenamiento de la sesión. */
const STORAGE_KEY = 'vinilos.session';

/** Sesión persistida. */
interface StoredSession {
  tokens: CognitoTokens;
  username: string;
}

/** Estado y acciones expuestas por el contexto de auth. */
export interface AuthState {
  /** ¿Hay sesión iniciada? */
  authenticated: boolean;
  /** ¿Se está cargando la sesión/permisos inicial? */
  loading: boolean;
  username: string | null;
  /** JWT (idToken) para la API. */
  idToken: string | null;
  /** Access token de Cognito (para operaciones como cambiar contraseña). */
  accessToken: string | null;
  /** Mapa de permisos del usuario (o `null` mientras carga/no autenticado). */
  permissions: MePermissions | null;
  /**
   * Guarda la sesión tras un login exitoso y carga permisos. `loginMethod`
   * (opcional) identifica el proveedor del inicio de sesión (`password` por
   * defecto; `google`/`apple`/`federated` en el flujo federado) para que el
   * rastro del login lo distinga.
   */
  setSession: (tokens: CognitoTokens, username: string, loginMethod?: string) => void;
  /** Cierra sesión (revoca en Cognito y limpia local). */
  signOut: () => Promise<void>;
  /** Recarga el mapa de permisos desde la API. */
  reloadPermissions: () => Promise<void>;
  /** ¿El usuario tiene al menos el nivel indicado en la funcionalidad? */
  hasPermission: (feature: Feature, level?: PermissionLevel) => boolean;
  /** ¿Es el propietario o el administrador del sistema (acceso total)? */
  isOwner: () => boolean;
  /** ¿Es el administrador del SISTEMA (crea owners/tenants)? */
  isSystemAdmin: () => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

function loadStored(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

/** Orden de niveles para comparar (none < read < read_write). */
const LEVEL_ORDER: Record<PermissionLevel, number> = { none: 0, read: 1, read_write: 2 };

/** Proveedor de auth. Envuelve la app. */
export function AuthProvider({
  children,
  fetchPermissions,
  recordLoginEvent,
}: {
  children: ReactNode;
  /** Carga los permisos usando el JWT (inyectado para desacoplar del api client). */
  fetchPermissions: (idToken: string) => Promise<MePermissions>;
  /**
   * Deja rastro del inicio de sesión en el log de actividad (inyectado para
   * desacoplar del api client). `method` identifica el proveedor del login
   * (`password` por defecto; `google`/`apple`/`federated`). Best-effort: nunca
   * debe romper el login.
   */
  recordLoginEvent?: (idToken: string, method?: string) => Promise<void>;
}) {
  const [session, setSessionState] = useState<StoredSession | null>(() => loadStored());
  const [permissions, setPermissions] = useState<MePermissions | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  /** Limpia la sesión local (sin llamar a Cognito). Se usa ante un 401. */
  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setSessionState(null);
    setPermissions(null);
  }, []);

  const loadPermissions = useCallback(
    async (idToken: string) => {
      try {
        const perms = await fetchPermissions(idToken);
        setPermissions(perms);
      } catch (err) {
        // Un 401 significa token expirado/ inválido: NO degradar a "sin permisos"
        // (eso mostraba al owner como "miembro" sin acceso). Cerrar la sesión para
        // volver al login y obtener un token fresco.
        if (err instanceof ApiError && err.status === 401) {
          clearSession();
          return;
        }
        // Errores transitorios (red/5xx): dejar null; la UI puede reintentar.
        setPermissions(null);
      }
    },
    [fetchPermissions, clearSession],
  );

  // Al montar (o si cambia la sesión), cargar permisos si hay token.
  useEffect(() => {
    let active = true;
    (async () => {
      if (session?.tokens.idToken) {
        await loadPermissions(session.tokens.idToken);
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [session, loadPermissions]);

  // Identificación en analítica (GA4/Hotjar) SIN PII: usamos el `username`
  // (sub de Cognito, identificador opaco) y el rol como dimensión. Al cerrar
  // sesión se limpia para no mezclar métricas entre usuarios. Es no-op si no
  // hay IDs de analítica configurados.
  useEffect(() => {
    if (session?.username && permissions?.role) {
      identifyUser({ userId: session.username, role: permissions.role });
    } else if (!session) {
      resetIdentity();
    }
  }, [session, permissions]);

  const setSession = useCallback(
    (tokens: CognitoTokens, username: string, loginMethod?: string) => {
      const stored: StoredSession = { tokens, username };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      setSessionState(stored);
      setLoading(true);
      // Rastro del login en el log de actividad (best-effort, sin bloquear). Solo
      // se dispara en un inicio de sesión real (setSession), no en la restauración
      // de la sesión al recargar ni en el refresh silencioso de tokens. El método
      // (contraseña vs. Google/Apple) solo lo conoce quien inicia la sesión, así
      // que se propaga para que el rastro distinga el proveedor y no quede todo
      // etiquetado como 'password'.
      if (recordLoginEvent) void recordLoginEvent(tokens.idToken, loginMethod);
    },
    [recordLoginEvent],
  );

  // Igual que `setSession` pero SIN activar el estado de carga: lo usa el
  // refresh proactivo de tokens para renovar la sesión en segundo plano sin
  // mostrar el spinner global ni recargar permisos (que no cambian al refrescar).
  const updateTokens = useCallback((tokens: CognitoTokens, username: string) => {
    const stored: StoredSession = { tokens, username };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    setSessionState(stored);
  }, []);

  const signOut = useCallback(async () => {
    const token = session?.tokens.accessToken;
    clearSession();
    if (token) await globalSignOut(token);
  }, [session, clearSession]);

  const reloadPermissions = useCallback(async () => {
    if (session?.tokens.idToken) await loadPermissions(session.tokens.idToken);
  }, [session, loadPermissions]);

  // Duración de sesión estándar: la sesión caduca tras 2 h de INACTIVIDAD. Cada
  // interacción del usuario renueva la cuenta atrás (lo gestiona el hook). Al
  // vencer, se cierra la sesión; eso vacía `authenticated` y `RequireAuth`
  // (App.tsx) redirige automáticamente al home.
  useIdleTimeout(session !== null, () => {
    void signOut();
  });

  // Renovación proactiva del token (Req. duración de sesión estándar). Los
  // tokens de Cognito (id/access) caducan a 1 h, pero el presupuesto de
  // inactividad es de 2 h: sin renovar, una petición fallaría con 401 a la hora
  // aunque el usuario siguiera activo. Programamos un refresh ~5 min ANTES de
  // que expire el idToken usando el refresh token almacenado; así la sesión se
  // mantiene viva mientras no se agote el tiempo de inactividad (que es quien
  // decide el cierre). Si el refresh falla por token revocado/expirado
  // (NotAuthorizedException), cerramos la sesión.
  useEffect(() => {
    const refreshToken = session?.tokens.refreshToken;
    const username = session?.username;
    if (!refreshToken || !username) return;

    // Margen de seguridad antes del vencimiento (5 min), acotado para tokens
    // cortos: nunca menos de 30 s desde ahora.
    const expiresInMs = (session.tokens.expiresIn || 3600) * 1000;
    const SKEW_MS = 5 * 60 * 1000;
    const delay = Math.max(expiresInMs - SKEW_MS, 30 * 1000);

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const next = await refreshTokens(refreshToken);
        if (!cancelled) updateTokens(next, username);
      } catch (err) {
        // Refresh token inválido/revocado/expirado: la sesión ya no es
        // recuperable sin volver a autenticarse. Cerrar sesión (→ redirect al
        // home). Errores transitorios de red se reintentan en el próximo ciclo.
        if (err instanceof CognitoError && err.type.includes('NotAuthorized')) {
          if (!cancelled) void signOut();
        }
      }
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [session, updateTokens, signOut]);

  const hasPermission = useCallback(
    (feature: Feature, level: PermissionLevel = 'read'): boolean => {
      if (!permissions) return false;
      // owner y system_admin tienen acceso total implícito.
      if (permissions.role === 'owner' || permissions.role === 'system_admin') return true;
      const current: PermissionLevel = permissions.permissions[feature] ?? 'none';
      return (LEVEL_ORDER[current] ?? 0) >= (LEVEL_ORDER[level] ?? 0);
    },
    [permissions],
  );

  // "isOwner" gobierna el acceso a la administración: owner de tenant o admin de
  // sistema (ambos pueden administrar usuarios de su tenant).
  const isOwner = useCallback(
    () => permissions?.role === 'owner' || permissions?.role === 'system_admin',
    [permissions],
  );

  const isSystemAdmin = useCallback(() => permissions?.role === 'system_admin', [permissions]);

  const value = useMemo<AuthState>(
    () => ({
      authenticated: session !== null,
      loading,
      username: session?.username ?? null,
      idToken: session?.tokens.idToken ?? null,
      accessToken: session?.tokens.accessToken ?? null,
      permissions,
      setSession,
      signOut,
      reloadPermissions,
      hasPermission,
      isOwner,
      isSystemAdmin,
    }),
    [session, loading, permissions, setSession, signOut, reloadPermissions, hasPermission, isOwner, isSystemAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook para consumir el contexto de auth. */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  return ctx;
}
